import { useEffect, useMemo, useState } from 'react'
import './VisitorQueue.css'

/** 'dd.mm.yyyy, hh:mm:ss' — the format the gatehouse list has always used. */
const stamp = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

// Which columns exist, and which a site sees until it says otherwise. The defaults are
// the gatehouse list as it has always looked; Name and Kennzeichen are new here —
// previously the Spedition cell fell back to the name, which meant a visitor with no
// haulier appeared in a column headed "Spedition".
const COLUMNS = [
  { key: 'reference',    label: 'Referenznummer', defaultVisible: true  },
  { key: 'name',         label: 'Name',           defaultVisible: false },
  { key: 'company',      label: 'Spedition',      defaultVisible: true  },
  { key: 'licensePlate', label: 'Kennzeichen',    defaultVisible: false },
  { key: 'phone',        label: 'Handynummer',    defaultVisible: true  },
  { key: 'email',        label: 'EMail',          defaultVisible: true  },
  { key: 'arrivedAt',    label: 'Ankunft',        defaultVisible: true  },
  { key: 'destination',  label: 'Status',         defaultVisible: true  },
  { key: 'flowMode',     label: 'Prozess',        defaultVisible: true  },
]

// Per user and per browser, like the Besucher page's column choice: two people watching
// the same gate want different columns, and that is a preference rather than tenant
// configuration. Unreadable or blocked storage falls back to the defaults.
const LS_VISIBLE = 'vq-col-visible'

function loadVisible() {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_VISIBLE) || '{}')
    return Object.fromEntries(COLUMNS.map(c => [c.key, saved[c.key] ?? c.defaultVisible]))
  } catch { return Object.fromEntries(COLUMNS.map(c => [c.key, c.defaultVisible])) }
}

/**
 * The queue view — the same visitors as the day grid, as a list.
 *
 * Offered because not every site thinks in ramps and time slots. A gatehouse with two
 * gates wants a vehicle list it can read top to bottom: reference number, haulier,
 * contact details, when they arrived, which gate they were sent to. The grid answers
 * "what is free when"; this answers "who is here and where did we send them", and for
 * many tenants that is the whole job.
 *
 * Reads the same board payload and calls the same assign/release endpoints — only the
 * presentation differs, so neither view can drift from the other's idea of the truth.
 */
export default function VisitorQueue({
  queue, destinations, destinationsEnabled, pages, showUnfinished = true,
  readOnly = false,
  busy, onAssignDestination, onAssignPage, onRelease,
}) {
  const [sort, setSort] = useState({ key: 'arrivedAt', dir: 'desc' })
  const [colVisible, setColVisible] = useState(loadVisible)
  const [pickerOpen, setPickerOpen] = useState(false)

  // Saved on change rather than on unmount: the page is often left by closing the tab,
  // and a choice that survives only a tidy exit is not a saved choice.
  useEffect(() => {
    try { localStorage.setItem(LS_VISIBLE, JSON.stringify(colVisible)) } catch { /* private mode */ }
  }, [colVisible])

  const toggleColumn = (key) => setColVisible(prev => ({ ...prev, [key]: !prev[key] }))

  // Never leave the table with no columns at all — an empty grid reads as a broken page
  // rather than as a choice, and there would be nothing left to click to undo it.
  const visibleCols = useMemo(() => {
    const shown = COLUMNS.filter(c => colVisible[c.key])
    return shown.length > 0 ? shown : COLUMNS.filter(c => c.defaultVisible)
  }, [colVisible])

  const toggleSort = (key) =>
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })

  const rows = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...queue].sort((a, b) => {
      const av = a[sort.key] ?? ''
      const bv = b[sort.key] ?? ''
      // Timestamps compare correctly as ISO strings; everything else is plain text,
      // where localeCompare keeps umlauts in the order a German reader expects.
      if (sort.key === 'arrivedAt') return av < bv ? -dir : av > bv ? dir : 0
      return String(av).localeCompare(String(bv), 'de', { numeric: true }) * dir
    })
  }, [queue, sort])

  // The visitor standing at the terminal right now: the one the gate attendant is
  // most likely talking to.
  //
  // This used to require `awaitingSince`, so the panel stayed empty until the visitor
  // had already reached the information step and was waiting — spending the whole lead
  // time this page exists to give staff on an empty card. A driver appears here as soon
  // as they finish a data page, which is the moment the kiosk announces them.
  //
  // Someone actually waiting still wins over someone merely in the flow: they are stuck
  // until a human answers, and their kiosk gives up at the step's timeout.
  const current = useMemo(() => {
    const waiting = queue.filter(v => v.awaitingSince)
    if (waiting.length > 0) {
      // Longest wait first — the one closest to running out of time. Not whoever the
      // current sort order happens to put on top.
      return waiting.reduce((a, b) => (a.awaitingSince <= b.awaitingSince ? a : b))
    }

    // Nobody is blocked, so this is whoever most recently walked up. Someone who began
    // earlier is still filling in forms and takes the panel back the moment they get
    // stuck on a step that needs an answer.
    const inFlow = queue.filter(v =>
      v.fromLiveSession && !v.checkedIn && !v.abandonedAt && !v.refusedAt)
    if (inFlow.length === 0) return null
    return inFlow.reduce((a, b) => ((a.arrivedAt ?? '') >= (b.arrivedAt ?? '') ? a : b))
  }, [queue])

  const sortMark = (key) => sort.key === key ? (sort.dir === 'asc' ? '▲' : '▼') : ''

  /**
   * Everything that is not a plain value: the held / freed / page pill, and the
   * markers for vorgemerkt, aus Vortag, a refused check-in, an abandoned one and one
   * still running. One cell, because together they are one answer to "what is going
   * on with this visitor" — and the wording matches the grid chips so the two views
   * cannot tell different stories.
   */
  const statusCell = (v) => (
    <>
    {/* A held destination is a pill, an info page is plain text, and
        nothing is a dash. Three states that mean different things —
        occupying a place, having been shown a page, and neither — so
        they no longer differ by font weight alone. */}
    {v.destination
      ? <span className="vq-status vq-status--held">{v.destination}</span>
      : v.lastDestination
        // Freed, but the day's record of where this visitor was does
        // not disappear with the assignment. Past tense and no pill,
        // so it can never be mistaken for a ramp still being held.
        ? <span
            className="vq-status vq-status--past"
            title={`Zielort freigegeben${v.lastDestinationReleasedAt ? ` um ${stamp(v.lastDestinationReleasedAt)}` : ''}`
                   + `${v.lastDestinationReleasedBy ? ` von ${v.lastDestinationReleasedBy}` : ''}`}
          >
            war: {v.lastDestination}
          </span>
        : v.shownPageName
          ? <span className="vq-status vq-status--page">{v.shownPageName}</span>
          : <span className="vq-status-none">—</span>}
    {v.reserved && <span className="vq-reserved">vorgemerkt</span>}
    {/* Still holding a destination from an earlier day. Says so
        rather than appearing as a today row with half its columns
        empty — there is no check-in from today to fill them. */}
    {v.carriedOver && (
      <span className="vq-carried" title={`Zuweisung vom ${v.visitDate}`}>
        aus Vortag
      </span>
    )}
    {/* Holding a destination with no completed check-in. Two cases,
        because they call for opposite reactions: still at the terminal
        is normal and needs nobody, while a session nobody is driving
        any more is a ramp held for somebody who may not be there. Same
        wording as the grid chip, so the two views agree. */}
    {/* The terminal reset itself or they pressed Abbrechen, and the
        kiosk said so. Two very different situations, told apart by
        whether this visitor had already been given their answer:

        shown something — they know where to go, and at a site where
        the gate is opened by hand after a call they are very likely
        standing there now. Calling that "abgebrochen" is wrong and
        would have staff chasing a driver who is already at the ramp.

        shown nothing — they stopped at the safety rules or earlier,
        with no information and no way in. */}
    {/* Refused by the server (Personenzulassung / Lieferavis) — checked
        first: such a visitor may also have been shown a page, and must
        not read as "Check-in offen, may be on site". */}
    {v.refusedAt && !v.checkedIn ? (
      <span
        className="vq-abandoned"
        title="Der Check-in wurde vom System abgelehnt. Der Besucher hat keine Zufahrt erhalten."
      >
        Check-in abgelehnt{v.refusedReason ? ` · ${v.refusedReason}` : ''}
        {' · '}{new Date(v.refusedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
      </span>
    ) : v.abandonedAt && !v.checkedIn ? (
      (v.shownPageName || v.destination) ? (
        <span
          className="vq-inflow-note"
          title={'Dem Besucher wurde seine Information bereits angezeigt, der '
               + 'Check-in wurde aber nicht abgeschlossen. Das heißt nicht, dass '
               + 'niemand da ist — wird das Tor nach einem Anruf von Hand '
               + 'geöffnet, steht der Fahrer dort.'}
        >
          Check-in offen
        </span>
      ) : (
        <span
          className="vq-abandoned"
          title={'Der Besucher hat den Vorgang abgebrochen oder ist weggegangen — '
               + 'das Terminal hat sich zurückgesetzt, bevor ihm etwas angezeigt '
               + 'wurde. Kein Check-in.'}
        >
          abgebrochen{v.abandonedStep ? ` · ${v.abandonedStep}` : ''}
          {' · '}{new Date(v.abandonedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
        </span>
      )
    ) : v.unfinishedCheckin && v.fromLiveSession && (
      <span
        className="vq-inflow-note"
        title="Der Besucher steht noch am Terminal. Der Check-in ist noch nicht abgeschlossen."
      >
        im Ablauf
      </span>
    )}
    {showUnfinished && v.unfinishedCheckin && !v.fromLiveSession && (
      <span
        className="vq-carried"
        title={'Der Check-in wurde nie abgeschlossen. Das heißt nicht zwingend, '
             + 'dass niemand da ist — wurde das Tor nach einem Anruf von Hand '
             + 'geöffnet, steht der Fahrer dort. Bitte prüfen und ggf. freigeben.'}
      >
        ohne Check-in — prüfen
      </span>
    )}
    </>
  )

  /**
   * One cell, keyed by column rather than written out in fixed order — the cells used to
   * sit in a fixed sequence, which is why a column could not be hidden without the rest
   * of the row silently shifting left into the wrong headings.
   */
  const cell = (key, v) => {
    switch (key) {
      case 'reference':
        return <td key={key} className="vq-ref">{v.reference || '—'}</td>
      case 'name':
        return <td key={key}>{v.name || '—'}</td>
      case 'company':
        // Still falls back to the name while Name is hidden, so a row never reads as
        // nameless; with both shown it stays strictly the haulier.
        return <td key={key}>{v.company || (colVisible.name ? '—' : (v.name || '—'))}</td>
      case 'licensePlate':
        return <td key={key} className="vq-plate">{v.licensePlate || '—'}</td>
      case 'phone':
        return <td key={key}>{v.phone || '—'}</td>
      case 'email':
        return <td key={key} className="vq-email">{v.email || '—'}</td>
      case 'arrivedAt':
        return <td key={key} className="vq-stamp">{stamp(v.arrivedAt)}</td>
      case 'flowMode':
        return <td key={key}>{v.flowMode || '—'}</td>
      case 'destination':
        return <td key={key}>{statusCell(v)}</td>
      default:
        return null
    }
  }

  return (
    <div className="vq">
      {/* ── Aktuelle Anmeldung ── */}
      <div className="card vq-current">
        <div className="section-title">Aktuelle Anmeldung</div>
        {current ? (
          <>
            {/* Which of the two this is. The panel now fills as soon as someone has
                entered their data, so without this line there is no telling whether
                this driver is blocked or simply still working through the flow. */}
            <div className="vq-current-state">
              {current.awaitingSince
                ? `Wartet auf Zuweisung seit ${stamp(current.awaitingSince)}`
                : 'Im Ablauf — Zuweisung kann jetzt schon gesetzt werden'}
            </div>
            <div className="vq-current-grid">
              <Field label="Referenznummer" value={current.reference} />
              <Field label="Spedition"      value={current.company} />
              <Field label="Handynummer"    value={current.phone} />
              <Field label="EMail"          value={current.email} />
            </div>
          </>
        ) : (
          // An empty panel would read as broken, so it says what it is waiting for.
          <p className="vq-empty">
            Derzeit ist niemand am Terminal angemeldet. Sobald ein Fahrer seine Daten
            eingegeben hat, erscheinen sie hier.
          </p>
        )}
      </div>

      {/* ── Warteschlange ── */}
      <div className="card vq-table-card">
        <div className="section-title vq-title">
          Warteschlange
          <span className="vq-badge">{queue.length}</span>

          {/* Which columns this person wants to see. Not every gatehouse cares about
              the same things — one works by reference number, another by plate — and
              the choice is personal rather than something to configure per tenant. */}
          <div className="vq-col-picker-wrap">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setPickerOpen(o => !o)}
            >
              Spalten ▾
            </button>
            {pickerOpen && (
              <>
                {/* Click anywhere else to close it. A popover that only closes via its
                    own button gets left open over the first row of the table. */}
                <div className="vq-col-picker-backdrop" onClick={() => setPickerOpen(false)} />
                <div className="vq-col-picker">
                  <div className="vq-col-picker-title">Spalten anzeigen</div>
                  {COLUMNS.map(col => (
                    <label key={col.key} className="vq-col-picker-item">
                      <input
                        type="checkbox"
                        checked={!!colVisible[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {queue.length === 0 ? (
          <p className="vq-empty">
            Für diesen Tag sind keine Fahrzeuge erfasst.
          </p>
        ) : (
          <div className="vq-scroll">
            <table className="vq-table">
              <thead>
                <tr>
                  {visibleCols.map(c => (
                    <th key={c.key} onClick={() => toggleSort(c.key)} className="vq-th">
                      {c.label} <span className="vq-sort">{sortMark(c.key)}</span>
                    </th>
                  ))}
                  <th className="vq-th vq-th--action">Zuweisung</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(v => (
                  <tr
                    key={v.sessionId}
                    className={`vq-row${v.awaitingSince ? ' vq-row--waiting' : ''}${v.checkedIn ? '' : ' vq-row--inflow'}`}
                  >
                    {visibleCols.map(c => cell(c.key, v))}

                    {/* One control per row, matching whichever kind of answer this
                        visitor's flow takes. A destination carries its own info page,
                        so the two are alternatives and never both appear. */}
                    <td className="vq-action">
                      {readOnly ? (
                        // Looking at an earlier day: nothing here is still a decision.
                        <span className="vq-status-none">—</span>
                      ) : destinationsEnabled && destinations.length > 0 ? (
                        <select
                          className="form-control vq-select"
                          value=""
                          disabled={busy === v.sessionId}
                          onChange={e => e.target.value && onAssignDestination(v, e.target.value)}
                        >
                          <option value="">
                            {v.destination ? '— umsetzen —' : '— Zielort zuweisen —'}
                          </option>
                          {destinations.map(d => (
                            <option key={d.id} value={d.id} disabled={d.full && d.id !== v.destinationId}>
                              {d.displayName}{d.full ? ' (voll)' : ''}
                            </option>
                          ))}
                        </select>
                      ) : v.pageAssignable && pages.length > 0 ? (
                        <select
                          className="form-control vq-select"
                          value={v.assignedPageId ?? ''}
                          disabled={busy === v.sessionId}
                          onChange={e => onAssignPage(v.sessionId, e.target.value)}
                        >
                          <option value="">— Seite zuweisen —</option>
                          {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : (
                        <span className="vq-status-none">—</span>
                      )}

                      {!readOnly && v.assignmentId && (
                        <button
                          className="vq-release"
                          disabled={busy === v.assignmentId}
                          onClick={() => onRelease(v.assignmentId)}
                          title="Zielort freigeben"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function Field({ label, value }) {
  return (
    <div className="vq-field">
      <div className="vq-field-label">{label}:</div>
      <div className="vq-field-value">{value || '—'}</div>
    </div>
  )
}
